import { useEnum } from "@/api/services/global/enum";
import { useGetNationalityList } from "@/api/services/global/master";
import { InnerBox } from "@/components/elements/InnerBox";
import { EMPTY_STR } from "@/constants/string";
import { TalentInfoItem } from "@/features/talent/components/talent-detail/TalentInfoItem";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black } from "@/theme/colors";
import { FCC } from "@/types/common";
import { findLabelByValue } from "@/utils/object";
import { Stack, Typography, Grid, Collapse } from "@mui/material";
import dayjs from "dayjs";
import { YEAR_MONTH_STR_FORMAT } from "@/constants/datetime";

type TTalentEducationBackgroundProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentEducationBackground: FCC<TTalentEducationBackgroundProps> = ({ defaultValues }) => {
  const { data: educationLevelOptions } = useEnum("EEducationLevel");
  const { data: foreignLanguageOptions } = useEnum("EForeignLanguage");
  const { nationalityOptions } = useGetNationalityList();
  const { expanded, toggle } = useCollapsible();

  const educationEntries = defaultValues?.educationBackground;
  const hasEducation = educationEntries && educationEntries.length > 0;

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <CollapsibleSectionHeader title="学歴・保有資格・語学" expanded={expanded} onToggle={toggle} />
      <Collapse in={expanded}>
      {hasEducation ? (
        educationEntries.map((entry, index) => {
          const educationLevel = entry?.educationLevelId ? findLabelByValue(educationLevelOptions, entry.educationLevelId) : "";
          const schoolName = entry?.schoolName ?? "";
          const countryName = entry?.countryId ? (findLabelByValue(nationalityOptions, entry.countryId) || entry.countryName || "") : "";
          const enrollmentDate = entry?.enrollmentDate ? dayjs(entry.enrollmentDate).format(YEAR_MONTH_STR_FORMAT) : "";
          const graduationDate = entry?.graduationDate ? dayjs(entry.graduationDate).format(YEAR_MONTH_STR_FORMAT) : "";

          const schoolLine = [schoolName, educationLevel ? `${educationLevel}` : ""].filter(Boolean).join(" ");
          const countryLine = countryName;
          const dateLine = enrollmentDate || graduationDate ? [enrollmentDate, graduationDate].filter(Boolean).join("〜") : "";
          const hasContent = schoolLine || countryLine || dateLine;

          return (
            <InnerBox key={index} mt={index === 0 ? 3 : 2} showDivider={false} p={2}>
              <Typography variant="body14Bold" color={black[30]}>
                学歴{index + 1}
              </Typography>

              {schoolLine && (
                <Stack>
                  <Typography variant="body14" color={black[30]} mt={1}>
                    {schoolLine} {countryLine ? `（${countryLine}）` : null}
                  </Typography>
                </Stack>
              )}

              {dateLine && (
                <Stack>
                  <Typography variant="cap12" color={black[70]} mt={0.5}>
                    {dateLine}
                  </Typography>
                </Stack>
              )}
              {!hasContent && <TalentInfoItem value={EMPTY_STR.TEXT} isShowDivider={false} />}
            </InnerBox>
          );
        })
        ) : (
          <Stack mt={3}>
            <Stack mb={1}>
              <Typography variant="cap12" color={black[70]}>
                学歴
              </Typography>
            </Stack>
            <TalentInfoItem value={EMPTY_STR.TEXT} />
          </Stack>
        )}
        <Stack mt={2} mb={2}>
          <Stack mb={1}>
            <Typography variant="cap12" color={black[70]}>
              資格
            </Typography>
          </Stack>
          {defaultValues?.heldQualifications?.length ? (
            defaultValues?.heldQualifications?.map((item, index) => (
              <TalentInfoItem
                key={index}
                value={item ?? EMPTY_STR.TEXT}
                isShowDivider={(defaultValues.heldQualifications?.length ?? 0) - 1 === index}
              />
            ))
          ) : (
            <TalentInfoItem value={EMPTY_STR.TEXT} />
          )}
        </Stack>
        {defaultValues?.languageSkills?.length ? (
          defaultValues?.languageSkills?.map((item, index) => (
            <InnerBox mt={index != 0 ? 2 : 1} showDivider={false} p={2}>
              <Typography variant="body14Bold" color={black[30]} mb={1}>
                語学{index + 1}
              </Typography>
              <Grid container>
                <Grid item xs={12}>
                  <Stack mb={1}>
                    <Typography variant="cap12" color={black[70]}>
                      語学（日本語以外の言語）
                    </Typography>
                  </Stack>
                  <TalentInfoItem
                    value={item?.language ? findLabelByValue(foreignLanguageOptions, item?.language) : EMPTY_STR.TEXT}
                    isShowDivider={false}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack mb={1} mt={2}>
                    <Typography variant="cap12" color={black[70]}>
                      語学（日本語以外の言語）の内容
                    </Typography>
                  </Stack>
                  <TalentInfoItem value={item?.content ?? EMPTY_STR.TEXT} isShowDivider={false} />
                </Grid>
              </Grid>
            </InnerBox>
          ))
        ) : (
          <InnerBox mt={2} showDivider={false} p={2}>
            <Stack mb={1}>
              <Typography variant="body14Bold" color={black[30]}>
                語学1
              </Typography>
            </Stack>
            <Grid container>
              <Grid item xs={12}>
                <Stack mb={1}>
                  <Typography variant="cap12" color={black[70]}>
                    語学（日本語以外の言語）
                  </Typography>
                </Stack>
                <TalentInfoItem value={EMPTY_STR.TEXT} isShowDivider={false} />
              </Grid>
              <Grid item xs={12}>
                <Stack mb={1} mt={2}>
                  <Typography variant="cap12" color={black[70]}>
                    語学（日本語以外の言語）の内容
                  </Typography>
                </Stack>
                <TalentInfoItem value={EMPTY_STR.TEXT} isShowDivider={false} />
              </Grid>
            </Grid>
          </InnerBox>
        )}
      </Collapse>
    </InnerBox>
  );
};
